import React, { useEffect, useState } from 'react';
import {
  Button,
  Col,
  Drawer,
  Form,
  Input,
  InputNumber,
  message,
  Select,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { EDIT_ERROR, REQUEST_LOADING, SUCCESS_UPDATE } from 'constants/alerts';
import { KassaService } from 'services/KassaService';
import { CONTENT_SPAN, LABEL_SPAN } from 'constants/table';
import { SectionTitle } from 'ui/SectionTitle';
import {
  kassaParnters,
  kassaStatuses,
  operator,
} from 'utils/handbooks/kassaHandbooks';
import { requiredRule } from 'utils/forms/validationRules';

function KassaForm({ clientId, isActive, initialValues, onClose, onSuccess }) {
  const [form] = Form.useForm();
  const [createdKassaId, setCreatedKassaId] = useState(null);
  const [ofdKassas, setOfdKassas] = useState([]);
  const [loading, setLoading] = useState(false);
  const formInitialValues = {
    status: 'NEW_APPLICATION',
    partner_name: 'UKASSA',
  };

  useEffect(() => {
    if (isActive && !initialValues) {
      createKassa();
      form.resetFields();
    } else if (isActive && initialValues) {
      const {
        ofd_credentials,
        kassa_credentials,
        remote_kassa_id,
        status,
        region,
        city,
        street,
        apartment,
      } = initialValues;

      const partner_email = kassa_credentials?.email;
      const partner_password = kassa_credentials?.password;

      if (partner_email && partner_password) getOfdKassas();

      form.setFieldsValue({
        status,
        remote_kassa_id,
        login: ofd_credentials?.login,
        password: ofd_credentials?.password,
        ofd_operator: ofd_credentials?.ofd_operator,
        token: ofd_credentials?.token,
        partner_email,
        partner_password,
        region,
        city,
        street,
        apartment,
      });
    } else {
      setOfdKassas([]);
    }
  }, [isActive, initialValues]);

  const createKassa = async () => {
    if (!clientId) return;

    const hide = message.loading('Создание кассы...');

    try {
      const { id } = await KassaService.submitApplication(clientId);

      setCreatedKassaId(id);
      message.success('Касса успешно создана, заполните данные');
    } catch (err) {
      message.error('Ошибка при создании кассы');
    } finally {
      hide();
    }
  };

  const getOfdKassas = async () => {
    const hide = message.loading('Загрузка партнерских касс...');
    setLoading(true);

    try {
      const ofd = await KassaService.getOfdKassas(
        createdKassaId || initialValues?.id
      );

      const kassas = ofd?.user_kassas?.kassa;

      setOfdKassas(kassas);
      message.success('Вы можете прикрепить кассу');
    } catch (err) {
      message.error('Не удалось загрузить партнерские кассы');
    } finally {
      setLoading(false);
      hide();
    }
  };

  const handleSubmit = async (values) => {
    const {
      status,
      login,
      password,
      ofd_operator,
      token,
      partner_name,
      partner_email,
      partner_password,
      remote_kassa_id,
      city,
      region,
      street,
      apartment,
    } = values;

    const payload = {
      status,
      city,
      region,
      street,
      apartment,
      remote_kassa_id,
    };

    if (login && password && ofd_operator) {
      payload.ofd_credentials = {
        login,
        password,
        ofd_operator,
        token,
      };
    }

    if (partner_email && partner_password) {
      payload.kassa_credentials = {
        email: partner_email,
        password: partner_password,
        partner: partner_name,
      };
    }

    const hide = message.loading(REQUEST_LOADING);

    try {
      const isUpdated = await KassaService.updateKassa(
        initialValues?.id || createdKassaId,
        payload
      );

      if (isUpdated) {
        message.success(SUCCESS_UPDATE);
        onSuccess();
        onClose();
        form.resetFields();
      }
    } catch (err) {
      message.error(EDIT_ERROR);
    } finally {
      hide();
    }
  };

  const handleSubmitPartnerCredentials = async () => {
    const status = form.getFieldValue('status');
    const partner_name = form.getFieldValue('partner_name');
    const partner_email = form.getFieldValue('partner_email');
    const partner_password = form.getFieldValue('partner_password');

    if (!partner_email || !partner_password) {
      message.warninging('Заполните корректно данные партнера');
      return;
    }

    const hide = message.loading(REQUEST_LOADING);
    const payload = {
      status,
      kassa_credentials: {
        email: partner_email,
        password: partner_password,
        partner: partner_name,
      },
    };

    try {
      const isUpdated = await KassaService.updateKassa(
        initialValues?.id || createdKassaId,
        payload
      );

      if (isUpdated) {
        await getOfdKassas();
        message.success(SUCCESS_UPDATE);
      }
    } catch (err) {
      message.error(EDIT_ERROR);
    } finally {
      hide();
    }
  };

  return (
    <Drawer
      title="Редактирование данных кассы"
      width="50%"
      onClose={onClose}
      visible={isActive}
      getContainer={false}
    >
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="horizontal"
        labelCol={{
          span: LABEL_SPAN,
        }}
        wrapperCol={{ span: CONTENT_SPAN }}
        labelAlign="right"
        initialValues={formInitialValues}
      >
        <Form.Item name="status" label="Статус" rules={[requiredRule]}>
          <Select placeholder="Выберите статус" allowClear>
            {kassaStatuses.map(({ text, value }) => (
              <Select.Option key={value} value={value}>
                {text}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <SectionTitle>ОФД</SectionTitle>

        <Form.Item name="login" label="Логин">
          <Input placeholder="Введите логин" />
        </Form.Item>

        <Form.Item name="password" label="Пароль">
          <Input placeholder="Введите пароль" />
        </Form.Item>

        <Form.Item name="token" label="Токен">
          <InputNumber
            controls={false}
            placeholder="Введите токен"
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item name="ofd_operator" label="Оператор">
          <Select placeholder="Выберите оператора" allowClear>
            {operator.map(({ text, value }) => (
              <Select.Option key={value} value={value}>
                {text}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <SectionTitle>Данные кассы партнера</SectionTitle>

        <Form.Item name="partner_name" label="Партнер">
          <Select placeholder="Выберите партнера">
            {kassaParnters.map(({ text, value }) => (
              <Select.Option key={value} value={value}>
                {text}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="partner_email" label="Email">
          <Input placeholder="Введите email партнера" />
        </Form.Item>

        <Form.Item name="partner_password" label="Пароль">
          <Input placeholder="Введите пароль партнера" />
        </Form.Item>

        <Form.Item name="remote_kassa_id" label="Касса">
          <Select placeholder="Выберите кассу" loading={loading} allowClear>
            {ofdKassas.map(({ id, name }) => (
              <Select.Option key={id} value={id}>
                {name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Col offset={CONTENT_SPAN}>
          <Button
            icon={<ReloadOutlined />}
            style={{ marginBottom: '20px' }}
            onClick={handleSubmitPartnerCredentials}
          >
            Обновить кассы парнера
          </Button>
        </Col>

        <SectionTitle>Адрес торговой точки</SectionTitle>

        <Form.Item name="region" label="Район">
          <Input placeholder="Введите название района" />
        </Form.Item>

        <Form.Item name="city" label="Город">
          <Input placeholder="Введите название города" />
        </Form.Item>

        <Form.Item name="street" label="Улица">
          <Input placeholder="Введите название улицы" />
        </Form.Item>

        <Form.Item name="apartment" label="Квартира">
          <InputNumber
            controls={false}
            placeholder="Введите номер кв/дом"
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item wrapperCol={{ offset: CONTENT_SPAN }}>
          <Button type="primary" htmlType="submit">
            Сохранить
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}

export { KassaForm };
